// frontend/src/pages/Tickets/PartnerTickets/PartnerTicketsList.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Tag, Space, Button, Input, Typography, Card, Modal, Dropdown, Menu, Select, Form, Spin, DatePicker } from 'antd';
import {
  SearchOutlined, DownloadOutlined, EyeOutlined,
  FilePdfOutlined, FileWordOutlined, SignatureOutlined, 
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../../../utils/api';
import '../TicketsList.css';
import { useNavigate } from 'react-router-dom';
import { useNotificationApi } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import { useContracts } from '../../../hooks/useContracts';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'unassigned': return 'red';
    case 'waiting for support': return 'orange';
    case 'in progress': return 'blue';
    case 'waiting for customer': return 'gold';
    case 'escalated': return 'purple';
    case 'resolved': return 'green';
    case 'closed': return 'default';
    case 'reopened': return 'volcano';
    case 'cancelled': return 'red';
    default: return 'default';
  }
};

// Helper to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high': return 'volcano';
    case 'medium': return 'gold';
    case 'low': return 'lime';
    case 'others': return 'purple';
    default: return 'purple';
  }
};

const PartnerTicketsList = () => {
  const [allFetchedTickets, setAllFetchedTickets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortInfo, setSortInfo] = useState({});
  const [activeView, setActiveView] = useState('all');
  const [initialStatusFilter, setInitialStatusFilter] = useState(null);
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();
  const { user } = useAuth();
  const [appSettings, setAppSettings] = useState({
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm'
  });

  // Get main contractor's contract IDs for filtering
  const userContractIds = user?.mainContractorContractIds || [];
  const isMainContractor = user?.isMainContractor === true && userContractIds.length > 0;

  // Fetch contract names
  const { contracts: userContracts, loading: loadingContracts, getContractName } = useContracts(userContractIds);
  
  // State for contract names mapping
  const [contractNames, setContractNames] = useState({});

  // States for signing functionality
  const [customerUsers, setCustomerUsers] = useState([]);
  const [loadingCustomerUsers, setLoadingCustomerUsers] = useState(false);
  const [signModalVisible, setSignModalVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [form] = Form.useForm();

  // Use refs to access current state in callbacks without dependencies
  const globalSearchTextRef = useRef(globalSearchText);
  const paginationRef = useRef(pagination);
  const columnFiltersRef = useRef(columnFilters);
  const sortInfoRef = useRef(sortInfo);
  const activeViewRef = useRef(activeView);
  const allFetchedTicketsRef = useRef(allFetchedTickets);

  // Update refs when state changes
  useEffect(() => {
    globalSearchTextRef.current = globalSearchText;
    console.log(`[PARTNER] globalSearchText updated to: "${globalSearchText}"`);
  }, [globalSearchText]);

  useEffect(() => {
    paginationRef.current = pagination;
    console.log(`[PARTNER] pagination updated to:`, pagination);
  }, [pagination]);

  useEffect(() => {
    allFetchedTicketsRef.current = allFetchedTickets;
  }, [allFetchedTickets]);

  // Update contract names mapping when contracts are loaded
  useEffect(() => {
    if (userContracts.length > 0) {
      const nameMap = {};
      userContracts.forEach(contract => {
        nameMap[contract.id] = contract.name;
      });
      setContractNames(nameMap);
      console.log('[PARTNER] Contract names loaded:', nameMap);
    }
  }, [userContracts]);

  // READ STATUS FROM URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status && !initialStatusFilter) {
      const decodedStatus = decodeURIComponent(status);
      console.log(`[PARTNER] Found status filter in URL: "${decodedStatus}"`);
      setInitialStatusFilter(decodedStatus);
      
      setColumnFilters(prev => ({
        ...prev,
        statusName: [decodedStatus]
      }));
      
      window.history.replaceState({}, '', '/partner/tickets');
    }
  }, [initialStatusFilter]);

  useEffect(() => {
    columnFiltersRef.current = columnFilters;
    console.log(`[PARTNER] columnFilters updated to:`, columnFilters);
  }, [columnFilters]);

  useEffect(() => {
    sortInfoRef.current = sortInfo;
    console.log(`[PARTNER] sortInfo updated to:`, sortInfo);
  }, [sortInfo]);

  useEffect(() => {
    activeViewRef.current = activeView;
    console.log(`[PARTNER] activeView updated to: "${activeView}"`);
  }, [activeView]);

  // Filter tickets based on user's contract IDs - USING CAMELCASE
  const filterTicketsByContract = useCallback((ticketsToFilter) => {
    if (!isMainContractor || userContractIds.length === 0) {
      console.log(`[PARTNER] Not a main contractor or no contracts, showing no tickets`);
      return [];
    }
    
    const filtered = ticketsToFilter.filter(ticket => {
      // API returns camelCase: contractId
      const ticketContractId = ticket.contractId;
      
      if (!ticketContractId) {
        console.log(`[PARTNER] Ticket ${ticket.ticketUuid} has no contractId`);
        return false;
      }
      
      const isInContract = userContractIds.includes(ticketContractId);
      
      if (!isInContract) {
        console.log(`[PARTNER] Filtered out ticket ${ticket.ticketUuid} - contract ${ticketContractId} not in user's contracts`);
      }
      
      return isInContract;
    });
    
    console.log(`[PARTNER] Filtered ${ticketsToFilter.length} tickets down to ${filtered.length} tickets for contracts:`, userContractIds);
    return filtered;
  }, [isMainContractor, userContractIds]);

  // Apply filters, search, and pagination to the filtered tickets
  const applyClientSideFilters = useCallback((filteredTickets) => {
    let result = [...filteredTickets];
    
    // Apply global search
    if (globalSearchTextRef.current) {
      const searchLower = globalSearchTextRef.current.toLowerCase();
      result = result.filter(ticket => 
        (ticket.ticketUuid && ticket.ticketUuid.toLowerCase().includes(searchLower)) ||
        (ticket.title && ticket.title.toLowerCase().includes(searchLower)) ||
        (ticket.creatorFirstName && ticket.creatorFirstName.toLowerCase().includes(searchLower)) ||
        (ticket.creatorLastName && ticket.creatorLastName.toLowerCase().includes(searchLower)) ||
        (ticket.assigneeFirstName && ticket.assigneeFirstName.toLowerCase().includes(searchLower)) ||
        (ticket.assigneeLastName && ticket.assigneeLastName.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply column filters
    if (columnFiltersRef.current) {
      const filters = columnFiltersRef.current;
      
      if (filters.statusName && filters.statusName.length > 0) {
        result = result.filter(ticket => 
          filters.statusName.some(status => 
            ticket.statusName?.toLowerCase().includes(status.toLowerCase())
          )
        );
      }
      
      if (filters.priorityName && filters.priorityName.length > 0) {
        result = result.filter(ticket => 
          filters.priorityName.some(priority => 
            ticket.priorityName?.toLowerCase().includes(priority.toLowerCase())
          )
        );
      }
    }
    
    // Apply sorting
    if (sortInfoRef.current && sortInfoRef.current.field) {
      const { field, order } = sortInfoRef.current;
      result.sort((a, b) => {
        let aVal = a[field];
        let bVal = b[field];
        
        if (field === 'caseReportedDate' || field === 'createdAt' || field === 'updatedAt') {
          aVal = aVal ? new Date(aVal) : new Date(0);
          bVal = bVal ? new Date(bVal) : new Date(0);
        } else if (field === 'creatorFirstName') {
          aVal = `${a.creatorFirstName || ''} ${a.creatorLastName || ''}`.trim();
          bVal = `${b.creatorFirstName || ''} ${b.creatorLastName || ''}`.trim();
        } else if (field === 'assigneeFirstName') {
          aVal = a.assigneeFirstName ? `${a.assigneeFirstName} ${a.assigneeLastName || ''}`.trim() : '';
          bVal = b.assigneeFirstName ? `${b.assigneeFirstName} ${b.assigneeLastName || ''}`.trim() : '';
        } else {
          aVal = aVal || '';
          bVal = bVal || '';
        }
        
        if (order === 'ascend') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }
    
    // Apply view-specific filtering
    const view = activeViewRef.current;
    const userId = user?.id;
    
    if (view === 'my') {
      result = result.filter(ticket => 
        ticket.createdByUserId === userId || ticket.assignedToUserId === userId
      );
    } else if (view === 'closed') {
      result = result.filter(ticket => 
        ticket.statusName?.toLowerCase() === 'closed'
      );
    } else if (view === 'to_sign') {
      result = result.filter(ticket => 
        ticket.statusName?.toLowerCase() === 'closed' && 
        !ticket.customerStandardSignedAt
      );
    }
    
    return result;
  }, [user]);

  // Update displayed tickets with pagination
  const updateDisplayedTickets = useCallback(() => {
    const filtered = applyClientSideFilters(allFetchedTicketsRef.current);
    const total = filtered.length;
    const { current, pageSize } = paginationRef.current;
    const start = (current - 1) * pageSize;
    const end = start + pageSize;
    const paginatedTickets = filtered.slice(start, end);
    
    setTickets(paginatedTickets);
    setPagination(prev => ({
      ...prev,
      total: total
    }));
    
    console.log(`[PARTNER] Displaying ${paginatedTickets.length} of ${total} tickets (page ${current})`);
  }, [applyClientSideFilters]);

  // Notification Helper Functions
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;
    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Function to fetch application settings
  const fetchAppSettings = useCallback(async () => {
    try {
      const response = await api.get('/api/settings');
      if (response.data && response.data.success) {
        setAppSettings(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('[PARTNER] Error fetching app settings:', error);
    }
  }, []);

  const getViewDisplayName = (viewType) => {
    const viewNames = {
      'all': 'All (Partner)',
      'my': 'My Created / Assigned',
      'closed': 'Closed',
      'to_sign': 'To Sign'
    };
    return viewNames[viewType] || 'All (Partner)';
  };

  // Fetch ALL tickets then filter on frontend
  const fetchTickets = useCallback(async (viewType = 'all', params = {}, triggeredByAction = false) => {
    console.log(`[PARTNER] Starting fetchTickets for view: "${viewType}"`, {
      userContractIds,
      isMainContractor
    });

    if (!isMainContractor) {
      console.warn('[PARTNER] User is not a main contractor, showing no tickets');
      setAllFetchedTickets([]);
      setTickets([]);
      setLoadingTickets(false);
      return;
    }

    setLoadingTickets(true);
    const loadingKey = `fetchPartnerTickets_${viewType}`;
    const hideLoading = showLoading(loadingKey, `Loading ${getViewDisplayName(viewType)} tickets...`);

    try {
      const response = await api.get('/api/tickets', { 
        params: { 
          page: 1, 
          pageSize: 1000
        } 
      });
      const responseData = response.data.data;
      
      console.log(`[PARTNER] Fetched ${Array.isArray(responseData) ? responseData.length : 0} total tickets`);
      
      const contractFilteredTickets = filterTicketsByContract(Array.isArray(responseData) ? responseData : []);
      setAllFetchedTickets(contractFilteredTickets);
      
      hideLoading();
      
      if (!triggeredByAction && contractFilteredTickets.length > 0) {
        showSuccess('Tickets Loaded', `${getViewDisplayName(viewType)} tickets fetched successfully!`);
      } else if (contractFilteredTickets.length === 0) {
        showSuccess('No Tickets', `No tickets found for your assigned contracts.`, 'noTicketsInfo');
      }
    } catch (error) {
      hideLoading();
      console.error('[PARTNER] Error fetching tickets:', error);
      showError('Failed to Fetch Tickets', error.response?.data?.message || 'An unexpected error occurred while fetching tickets.');
      setAllFetchedTickets([]);
      setTickets([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoadingTickets(false);
    }
  }, [filterTicketsByContract, isMainContractor, userContractIds, showLoading, showSuccess, showError]);

  // Update displayed tickets when dependencies change
  useEffect(() => {
    if (allFetchedTickets.length > 0 || !loadingTickets) {
      updateDisplayedTickets();
    }
  }, [allFetchedTickets, globalSearchText, columnFilters, sortInfo, activeView, pagination.current, pagination.pageSize, updateDisplayedTickets, loadingTickets]);

  // Signing functionality
  const fetchCustomerUsers = useCallback(async () => {
    setLoadingCustomerUsers(true);
    try {
      const response = await api.get('/api/users/customers');
      if (response.data.success) {
        setCustomerUsers(response.data.data);
      }
    } catch (error) {
      showError('Fetch Error', 'Failed to load customer users for signing');
    } finally {
      setLoadingCustomerUsers(false);
    }
  }, [showError]);

  const handleSign = async (ticketId, nextSignerId = null) => {
    const loadingKey = `signTicket_${ticketId}`;
    const hideLoading = showLoading(loadingKey, 'Signing ticket...');

    try {
      const payload = nextSignerId ? { nextSignerId } : {};
      await api.post(`/api/tickets/${ticketId}/sign`, payload);
      
      hideLoading();
      showSuccess('Ticket Signed', 'Ticket has been successfully signed.');
      
      await fetchTickets(activeView, {}, true);
      
      setSignModalVisible(false);
      setSelectedTicket(null);
      form.resetFields();
      
    } catch (error) {
      hideLoading();
      let errorMessage = 'An unexpected error occurred while signing the ticket.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showError('Sign Error', errorMessage);
    }
  };

  const openSignModal = (ticket) => {
    setSelectedTicket(ticket);
    setSignModalVisible(true);
    fetchCustomerUsers();
  };

  const handleDirectSign = (ticketId) => {
    handleSign(ticketId);
  };

  const handleModalSign = () => {
    form.validateFields().then(values => {
      if (selectedTicket && values.customerUser) {
        handleSign(selectedTicket.id, values.customerUser);
      }
    });
  };

  const needsCustomerSelection = () => {
    const userRole = user?.roleName;
    return [
      'Internal Staff Supervisor',
      'Partner Supervisor',
      'System Admin'
    ].includes(userRole);
  };

  const handleGlobalSearch = (value) => {
    console.log(`[PARTNER] handleGlobalSearch triggered with value: "${value}"`);
    setGlobalSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewChange = (viewType) => {
    console.log(`[PARTNER] Changing view from "${activeView}" to "${viewType}"`);
    setActiveView(viewType);
    setGlobalSearchText('');
    setColumnFilters({});
    setSortInfo({});
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchTickets(viewType, {}, true);
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    console.log(`[PARTNER] Table change event:`, { pagination: newPagination, filters, sorter });

    const mappedFilters = {};
    for (const key in filters) {
      if (filters[key] && filters[key].length > 0) {
        mappedFilters[key] = filters[key];
      }
    }
    
    setColumnFilters(mappedFilters);

    let newSortInfo = {};
    if (sorter && sorter.field) {
      newSortInfo = {
        field: sorter.field,
        order: sorter.order
      };
    }
    
    setSortInfo(newSortInfo);

    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/tickets/${ticketId}`);
  };

  const handleManualRefresh = () => {
    console.log(`[PARTNER] Manual refresh triggered for view: "${activeView}"`);
    fetchTickets(activeView, {}, true);
    showSuccess('Refreshed', 'Ticket list refreshed successfully.');
  };

  const handleDownload = async (ticketUuid, format) => {
    const loadingKey = `download${format.toUpperCase()}Loading`;
    const hideLoading = showLoading(loadingKey, `Generating ${format.toUpperCase()}...`);

    try {
      const response = await api.get(`/api/reports/tickets/${ticketUuid}/borang-aduan`, {
        params: { format },
        responseType: 'blob',
        timeout: 60000,
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = `BorangAduan_${ticketUuid}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8''|\w*')?([^"'\r\n]+)['"]?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      const contentType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      hideLoading();
      showSuccess(`${format.toUpperCase()} Generated`, `Borang Aduan for ticket ${ticketUuid} downloaded successfully!`);
    } catch (error) {
      hideLoading();
      let errorMessage = `An unexpected error occurred while generating the ${format.toUpperCase()}.`;
      if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (parseError) {
          errorMessage = errorText || errorMessage;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showError(`Failed to Download ${format.toUpperCase()}`, errorMessage);
    }
  };

  const getColumnSearchProps = (dataIndex, placeholderText) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${placeholderText}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#4299e1' : undefined }} />,
    onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
  });

  const baseColumns = [
    {
      title: 'TICKET NUMBER',
      dataIndex: 'ticketUuid',
      key: 'ticketUuid',
      render: (text, record) => (
        <a href={`/tickets/${record.id}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#4299e1', fontSize: '13px', textDecoration: 'none' }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
          {text}
        </a>
      ),
      ...getColumnSearchProps('ticketUuid', 'ticket number'),
      sorter: true,
      fixed: 'left',
      width: 180,
    },
    {
      title: 'CONTRACT',
      dataIndex: 'contractId',
      key: 'contract',
      render: (contractId) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: 500 }}>
          {contractNames[contractId] || `Contract ${contractId}`}
        </Tag>
      ),
      width: 200,
    },
    {
      title: 'SUMMARY',
      dataIndex: 'title',
      key: 'summary',
      ...getColumnSearchProps('title', 'summary'),
      sorter: true,
      ellipsis: true,
      width: 300,
      render: (text) => <Text style={{ fontSize: '14px', lineHeight: '1.4', fontWeight: 500 }}>{text}</Text>
    },
    {
      title: 'REPORTER',
      dataIndex: 'creatorFirstName',
      key: 'reporter',
      render: (text, record) => (
        <div style={{ fontWeight: 500, fontSize: '14px' }}>
          {`${record.creatorFirstName || ''} ${record.creatorLastName || ''}`.trim() || `User ${record.createdByUserId}`}
        </div>
      ),
      ...getColumnSearchProps('creatorFirstName', 'reporter'),
      sorter: true,
      width: 180,
    },
    {
      title: 'ASSIGNEE',
      dataIndex: 'assigneeFirstName',
      key: 'assignee',
      render: (text, record) => (
        <div style={{ fontWeight: 500, fontSize: '14px' }}>
          {record.assigneeFirstName ? 
            `${record.assigneeFirstName || ''} ${record.assigneeLastName || ''}`.trim() : 
            record.assignedToUserId ? `User ${record.assignedToUserId}` :
            <Tag color="default">UNASSIGNED</Tag>
          }
        </div>
      ),
      ...getColumnSearchProps('assigneeFirstName', 'assignee'),
      sorter: true,
      width: 180,
    },
    {
      title: 'STATUS',
      dataIndex: 'statusName',
      key: 'statusName',
      filters: activeView !== 'to_sign' ? [
        { text: 'Unassigned', value: 'Unassigned' },
        { text: 'Waiting For Support', value: 'Waiting For Support' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Waiting For Customer', value: 'Waiting For Customer' },
        { text: 'Escalated', value: 'Escalated' },
        { text: 'Resolved', value: 'Resolved' },
        { text: 'Closed', value: 'Closed' },
        { text: 'Reopened', value: 'Reopened' },
        { text: 'Cancelled', value: 'Cancelled' },
      ] : null,
      render: (status) => <Tag color={getStatusColor(status)} key={status}>{status?.toUpperCase()}</Tag>,
      width: 180,
    },
    {
      title: 'PRIORITY',
      dataIndex: 'priorityName',
      key: 'priorityName',
      filters: [
        { text: 'High', value: 'High' },
        { text: 'Medium', value: 'Medium' },
        { text: 'Low', value: 'Low' },
        { text: 'Others', value: 'Others' },
      ],
      render: (priority) => <Tag color={getPriorityColor(priority)} key={priority}>{priority?.toUpperCase()}</Tag>,
      width: 120,
    },
    {
      title: 'REPORTED DATE',
      dataIndex: 'caseReportedDate',
      key: 'caseReportedDate',
      sorter: true,
      render: (text) => (
        <div style={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
          {text ? moment(text).format(`${appSettings.dateFormat} ${appSettings.timeFormat}`) : 'N/A'}
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        let startDate = null;
        let endDate = null;
        
        try {
          if (selectedKeys && selectedKeys[0]) {
            const parsed = JSON.parse(selectedKeys[0]);
            if (Array.isArray(parsed)) {
              startDate = parsed[0];
              endDate = parsed[1];
            }
          }
        } catch (error) {
          console.error('Error parsing date filter:', error);
        }
        
        const handleDateChange = (dates) => {
          if (dates) {
            const [start, end] = dates;
            const startStr = start ? start.toISOString() : null;
            const endStr = end ? end.toISOString() : null;
            setSelectedKeys([JSON.stringify([startStr, endStr])]);
          } else {
            setSelectedKeys([]);
          }
        };
        
        const rangeValue = startDate && endDate 
          ? [moment(startDate), moment(endDate)]
          : startDate 
            ? [moment(startDate), null]
            : endDate
              ? [null, moment(endDate)]
              : null;
        
        return (
          <div style={{ padding: 8, width: 320 }}>
            <div style={{ marginBottom: 8 }}>
              <DatePicker.RangePicker
                value={rangeValue}
                onChange={handleDateChange}
                format={appSettings.dateFormat}
                style={{ width: '100%' }}
                placeholder={['Start Date', 'End Date']}
                allowClear={true}
              />
            </div>
            <Space>
              <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Filter</Button>
              <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#4299e1' : undefined }} />,
      width: 200,
    },
  ];

  const signingColumns = [
    {
      title: 'SUPERVISOR SIGNED',
      dataIndex: 'supervisorSignedAt',
      key: 'supervisorSignedAt',
      render: (text) => text ? <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag> : <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>,
      width: 150,
    },
    {
      title: 'CUSTOMER SIGNED',
      dataIndex: 'customerStandardSignedAt',
      key: 'customerStandardSignedAt',
      render: (text) => text ? <Tag color="green" icon={<CheckCircleOutlined />}>SIGNED</Tag> : <Tag color="red" icon={<CloseCircleOutlined />}>PENDING</Tag>,
      width: 150,
    },
  ];

  const getActionColumn = () => ({
    title: 'ACTIONS',
    key: 'actions',
    render: (_, record) => (
      <Space size="small">
        <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewTicket(record.id)} style={{ padding: '6px 12px', fontSize: '13px', height: 'auto', fontWeight: 600 }}>View</Button>
        <Dropdown
          overlay={<Menu onClick={({ key }) => handleDownload(record.ticketUuid, key)} items={[{ key: 'pdf', label: 'Download as PDF', icon: <FilePdfOutlined /> }, { key: 'docx', label: 'Download as Word', icon: <FileWordOutlined /> }]} />}
          trigger={['click']}>
          <Button type="link" icon={<DownloadOutlined />} style={{ padding: '6px 12px', fontSize: '13px', height: 'auto', fontWeight: 600 }}>Export</Button>
        </Dropdown>
        {activeView === 'to_sign' && (
          <Button type="link" icon={<SignatureOutlined />} onClick={() => needsCustomerSelection() ? openSignModal(record) : handleDirectSign(record.id)} style={{ padding: '6px 12px', fontSize: '13px', height: 'auto', fontWeight: 600 }}>Sign</Button>
        )}
      </Space>
    ),
    fixed: 'right',
    width: activeView === 'to_sign' ? 220 : 180,
  });

  const getColumns = () => {
    let columns = [...baseColumns];
    if (activeView === 'to_sign') {
      columns = [...columns, ...signingColumns];
    }
    columns.push(getActionColumn());
    return columns;
  };

  // Initial fetch
  useEffect(() => {
    if (isMainContractor && userContractIds.length > 0) {
      fetchTickets(activeView, {
        pagination: { current: 1, pageSize: pagination.pageSize },
        globalSearchText,
        columnFilters,
        sortInfo
      });
      fetchAppSettings();
    } else if (!isMainContractor) {
      setLoadingTickets(false);
    }
  }, []);

  // Conditional rendering at the END of the component
  if (!isMainContractor) {
    return (
      <Card className="tickets-list-page-card">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Access Restricted</Title>
          <Text type="secondary">This page is only for main contractors with assigned contracts.</Text>
        </div>
      </Card>
    );
  }

  if (isMainContractor && userContractIds.length === 0) {
    return (
      <Card className="tickets-list-page-card">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>No Contracts Assigned</Title>
          <Text type="secondary">You don't have any contracts assigned. Please contact your administrator.</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card className="tickets-list-page-card">
      <div className="tickets-list-header">
        <Title level={2} className="tickets-list-title">Partner Ticket Management</Title>
        <Text type="secondary" className="tickets-list-description">
          {loadingContracts ? 'Loading contract information...' : 
            userContracts.length > 0 ? `Viewing tickets for: ${userContracts.map(c => c.name).join(', ')}` : 
            'No contracts assigned'}
        </Text>
      </div>

      <Modal title="Select Customer User for Next Signature" open={signModalVisible} onCancel={() => { setSignModalVisible(false); setSelectedTicket(null); form.resetFields(); }} onOk={handleModalSign} okText="Sign and Forward to Customer" cancelText="Cancel" width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="customerUser" label="Select Customer User" rules={[{ required: true, message: 'Please select a customer user to forward the ticket to' }]}>
            <Select placeholder="Search and select a customer user..." loading={loadingCustomerUsers} showSearch optionFilterProp="children" notFoundContent={loadingCustomerUsers ? <Spin size="small" /> : "No customer users found"}>
              {customerUsers.map(user => (<Option key={user.id} value={user.id}>{`${user.firstName} ${user.lastName} - ${user.organizationName || 'No Organization'} (${user.roleName})`}</Option>))}
            </Select>
          </Form.Item>
          <Text type="secondary">After you sign, the ticket will be forwarded to the selected customer user for their signature.</Text>
        </Form>
      </Modal>

      <div className="tickets-table-filters-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <Select value={activeView} onChange={handleViewChange} style={{ width: 200 }} size="large">
            <Option value="all">All Tickets (Partner)</Option>
            <Option value="my">My Created / Assigned Tickets</Option>
            <Option value="closed">Closed Tickets</Option>
            <Option value="to_sign">Tickets To Sign</Option>
          </Select>

          <Search placeholder={`Search ${getViewDisplayName(activeView).toLowerCase()} tickets...`} enterButton="Search" value={globalSearchText} onChange={(e) => setGlobalSearchText(e.target.value)} onSearch={handleGlobalSearch} onPressEnter={(e) => { e.preventDefault(); handleGlobalSearch(globalSearchText); }} style={{ width: 400 }} className="tickets-global-search" size="large" />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="tickets-stats-counter">{pagination.total} {getViewDisplayName(activeView)} Tickets</div>
          <Button icon={<ReloadOutlined />} onClick={handleManualRefresh} size="large">Refresh</Button>
        </div>
      </div>

      <div className="tickets-table-container">
        <Table 
          columns={getColumns()} 
          dataSource={tickets} 
          rowKey="id" 
          pagination={pagination} 
          loading={loadingTickets} 
          onChange={handleTableChange} 
          scroll={{ x: 'max-content' }} 
          className="tickets-ant-table" 
          size="middle" 
        />
      </div>
    </Card>
  );
};

export default PartnerTicketsList;